import get from 'lodash-es/get'
import * as raf from 'raf'
import * as React from 'react'
import * as VisibilitySensor from 'react-visibility-sensor'

import { reportAudioPlayClick } from '../../../../../common/analytics/resultAnalytics'
import axiosVerbose from '../../../../../common/utils/axios/axiosVerbose'
import { logError } from '../../../../../common/utils/errors'
import pad from '../../../../../common/utils/pad'
import { getMinutesSecondsTime } from '../../../../../common/utils/time'
import { IItem } from '../../../../../typings/searchResults'
import SpinnerIcon from '../../../../components/icons/loadingSpinner'
// TODO: these should be renamed to "pause" and "play" once the
// originals have been removed. LJ
import PauseIcon from '../../../../components/icons/pauseUpdated'
import PlayIcon from '../../../../components/icons/playUpdated'
import Waveform from './waveform'

import * as styles from './audioPlayer.module.css'

interface IProps {
  item: IItem
  onPlay: () => void
  isActive: boolean
  viewType: string
  searchAudioMuted: boolean
  searchAudioVolumeLevel: number
}

interface IState {
  playing: boolean
  bufferProgress: number
  playBarProgress: number
  expectedPlayBarProgress: any
  dragging: boolean
  loading: boolean
  seen: boolean
  waveformData: any
  playPromise: any
}

export default class AudioPlayer extends React.Component<IProps, IState> {
  private unmounted: boolean
  private audioElement: HTMLAudioElement
  private waveformContainerElement: HTMLDivElement

  constructor(props: IProps) {
    super(props)

    this.state = {
      playing: false,
      bufferProgress: 0,
      playBarProgress: 0,
      expectedPlayBarProgress: null,
      dragging: false,
      loading: false,
      seen: false,
      waveformData: null,
      playPromise: null
    }

    this.togglePlay = this.togglePlay.bind(this)
    this.onAudioBuffer = this.onAudioBuffer.bind(this)
    this.onAudioProgress = this.onAudioProgress.bind(this)
    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onSeeked = this.onSeeked.bind(this)
    this.onDurationChange = this.onDurationChange.bind(this)
    this.onPlay = this.onPlay.bind(this)
    this.onPause = this.onPause.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onElementVisible = this.onElementVisible.bind(this)
    this.setAudioElementRef = this.setAudioElementRef.bind(this)
    this.setWaveformContainerElementRef = this.setWaveformContainerElementRef.bind(
      this
    )
  }

  public componentWillUnmount() {
    this.unmounted = true
  }

  public componentDidMount() {
    if (this.audioElement) {
      this.updateAudioVolume()
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    const { searchAudioVolumeLevel } = this.props
    if (this.audioElement) {
      if (prevProps.isActive && !this.props.isActive) {
        this.tryPause()
      }
      if (searchAudioVolumeLevel !== prevProps.searchAudioVolumeLevel) {
        this.updateAudioVolume()
      }
    }
  }

  public render() {
    const {
      playing,
      loading,
      seen,
      waveformData,
      bufferProgress,
      playBarProgress
    } = this.state
    const { item, viewType } = this.props
    let buttonIcon = null
    if (loading) {
      buttonIcon = <SpinnerIcon className={styles.spinningIcon} />
    } else {
      if (playing) {
        buttonIcon = <PauseIcon className={styles.icon} />
      } else {
        buttonIcon = <PlayIcon className={styles.icon} />
      }
    }

    const mp3Url = get(item, 'previews.icon_with_audio_preview.mp3_url')
    const length = get(item, 'previews.icon_with_audio_preview.length')
    const trackLengthHours = length && length.hours ? `${length.hours}:` : ''
    const trackLength = length
      ? `${trackLengthHours}${pad(length.minutes, 2)}:${pad(length.seconds, 2)}`
      : null
    return (
      <div className={styles.spaceBetween}>
        <VisibilitySensor onChange={this.onElementVisible} active={!seen} />
        <audio
          ref={this.setAudioElementRef}
          preload="none"
          onPlay={this.onPlay}
          onPause={this.onPause}
          onDurationChange={this.onDurationChange}
          onProgress={this.onAudioBuffer}
          onSeeked={this.onSeeked}
          onCanPlay={this.onSeeked}
          muted={this.props.searchAudioMuted}
        >
          <source src={mp3Url} type="audio/mpeg" />
        </audio>
        <button onClick={this.togglePlay} className={styles.button}>
          {buttonIcon}
        </button>
        <div
          role="presentation"
          ref={this.setWaveformContainerElementRef}
          className={styles.waveformContainer}
          onMouseDown={this.onMouseDown}
          onMouseUp={this.onMouseUp}
          onMouseMove={this.onMouseMove}
          onMouseLeave={this.onMouseLeave}
        >
          {waveformData && (
            <React.Fragment>
              <Waveform
                data={waveformData}
                colour="hsl(0, 0%, 80%)"
                progress={1}
                viewType={viewType}
              />
              <Waveform
                data={waveformData}
                colour="hsl(0, 0%, 60%)"
                progress={bufferProgress}
                viewType={viewType}
              />
              <Waveform
                data={waveformData}
                colour="hsl(87, 58%, 60%)"
                progress={playBarProgress}
                viewType={viewType}
              />
            </React.Fragment>
          )}
          <div
            style={{
              width: `${playBarProgress * 100}%`,
              height: '100%',
              position: 'absolute',
              boxShadow: '2px 0px 0px hsl(87, 58%, 40%)',
              display: playBarProgress > 0 ? undefined : 'none'
            }}
          >
            <div className={styles.timeContainer}>
              {this.audioElement
                ? getMinutesSecondsTime(this.audioElement.currentTime)
                : ''}
            </div>
          </div>
        </div>
        <span className={styles.audioLength}>{trackLength}</span>{' '}
      </div>
    )
  }

  private updateAudioVolume() {
    const { searchAudioVolumeLevel } = this.props
    this.audioElement.volume = searchAudioVolumeLevel / 100
  }

  private onElementVisible(isVisible: boolean) {
    const { item } = this.props
    const { seen } = this.state
    if (!seen && isVisible) {
      this.setState({ seen: true })
      // We get a url that looks like the following: https://0.s3.envato.com/files/88545/preview.mp3?foo=bar
      // This regex will extract the 88545/preview.mp3 part from it and send that to tsunami
      const mp3UrlRegex = /^.*files\/(.*\.mp3)/g
      const mp3Url = get(item, 'previews.icon_with_audio_preview.mp3_url')
      const mp3PathGroup = mp3UrlRegex.exec(mp3Url)
      let mp3Path
      if (mp3PathGroup && mp3PathGroup.length >= 1) {
        mp3Path = mp3PathGroup[1]
      }

      axiosVerbose
        .get(`https://waveform-discovery.envato.com/tsunami/${mp3Path}`)
        .then((response: any) => {
          if (!this.unmounted) {
            this.setState({ waveformData: response.data })
          }
        })
        .catch(logError)
    }
  }

  private onPause() {
    this.setState({ playing: false })
  }

  private onPlay() {
    reportAudioPlayClick(get(this.props, 'item.id'))
    this.props.onPlay()
    this.setState({ playing: true })
    raf(this.onAudioProgress)
  }

  private tryPause() {
    if (this.state.playPromise) {
      this.state.playPromise.then(() => {
        if (this.audioElement) {
          this.audioElement.pause()
        }
      })
    }
  }

  private tryPlay() {
    if (this.audioElement) {
      /*
        For some old browsers (Safari 9) play() does not return promise but undefined.
        Let's handle that case by returning resolved promise instead.
       */
      return (this.audioElement.play() || Promise.resolve()).catch(error => {
        // Throw error only if HTMLMediaElement still exists, if it's unmounted let's ignored it.
        // HTMLMediaElement internally call pause when removed from DOM. If media is still loading when unmounted
        // it caused an error "play() interrupted by pause()". There is no way to wait for media to play in the process of
        // unmounting
        if (this.audioElement) {
          throw error
        }
      })
    }

    return
  }

  private onDurationChange() {
    if (this.state.expectedPlayBarProgress) {
      this.audioElement.currentTime =
        this.state.expectedPlayBarProgress * this.audioElement.duration
    }
  }

  private onSeeked() {
    this.showLoading()
  }

  private onMouseDown(event: any) {
    const progress =
      event.nativeEvent.offsetX / this.waveformContainerElement.clientWidth
    this.setState({ dragging: true, playBarProgress: progress })
  }

  private onMouseUp() {
    const { playBarProgress } = this.state

    this.setState({ dragging: false })

    if (this.audioElement.duration) {
      this.audioElement.currentTime =
        playBarProgress * this.audioElement.duration
      this.setState({
        playPromise: this.tryPlay()
      })

      this.showLoading()
    } else {
      this.setState(
        {
          expectedPlayBarProgress: playBarProgress
        },
        () => {
          this.setState({
            playPromise: this.tryPlay()
          })
          this.showLoading()
        }
      )
    }
  }

  private onMouseMove(event: any) {
    const { dragging } = this.state
    if (dragging) {
      this.setState({
        playBarProgress:
          event.nativeEvent.offsetX / this.waveformContainerElement.clientWidth
      })
    }
  }

  private onMouseLeave() {
    this.setState({
      dragging: false
    })
  }

  private onAudioBuffer() {
    if (this.audioElement.buffered.length > 0 && this.audioElement.duration) {
      let percentLoaded =
        this.audioElement.buffered.end(this.audioElement.buffered.length - 1) /
        this.audioElement.duration
      if (percentLoaded > 1) {
        percentLoaded = 1
      }
      this.setState({ bufferProgress: percentLoaded })
    }
  }

  private onAudioProgress() {
    if (this.state.playing) {
      if (!this.state.dragging && this.audioElement) {
        this.setState({
          playBarProgress:
            this.audioElement.currentTime / this.audioElement.duration
        })
      }
      raf(this.onAudioProgress)
    }
  }

  private showLoading() {
    if (this.audioElement.seeking || this.audioElement.readyState < 3) {
      this.setState({ loading: true })
    } else {
      this.setState({ loading: false })
    }
  }

  private togglePlay() {
    const { playing } = this.state
    if (this.audioElement) {
      const isTrulyPlaying =
        this.audioElement.currentTime > 0 &&
        !this.audioElement.paused &&
        !this.audioElement.ended &&
        this.audioElement.readyState > 2
      if (playing && isTrulyPlaying) {
        this.tryPause()
      } else if (!isTrulyPlaying) {
        this.setState({
          playPromise: this.tryPlay()
        })
        this.showLoading()
      }
    }
  }

  private setAudioElementRef(audioElement: HTMLAudioElement) {
    this.audioElement = audioElement
  }

  private setWaveformContainerElementRef(waveformElement: HTMLDivElement) {
    this.waveformContainerElement = waveformElement
  }
}
